import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Typography, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { TransferIcon } from '~/img';
import theme from '~/theme';
import { Consent, SendMoneyState } from '~/types';
import { debounce } from 'lodash';
import {
  createSendMoneyConsent,
  SendMoneyBeneficiary,
  SendMoneyConsentData,
  updateSendMoneyConsent,
} from '~/api/sendMoney';
import FromAccount from '~/components/sendMoney/FromAccount';
import ViewMoreDetails from './ViewMoreDetails';
import ConsentDetails from './ConsentDetails';
import { getAmountText } from '~/components/fields/AmountField';
import SendAmount from '~/components/sendMoney/SendAmount';
import styled from 'styled-components';

interface ConfirmationProps {
  mainState: SendMoneyState;
  onTransfer: () => void;
  setMainState: React.Dispatch<React.SetStateAction<SendMoneyState>>;
  beneficiaries: SendMoneyBeneficiary[];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const Wrapper = styled(Space)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 760px;
  flex-grow: 1;
  .ant-space-item {
    width: 100%;
  }
`;

const Confirmation: FC<ConfirmationProps> = ({
  mainState,
  setMainState,
  onTransfer,
  beneficiaries,
  isLoading,
  setIsLoading,
}) => {
  const { selectedBeneficiary, recipientAccountNumber, recipientPhoneNumber } = mainState;
  const { t } = useTranslation('sendMoney');
  const [form] = Form.useForm();

  const [consent, setConsent] = useState<Consent>({
    id: '',
  } as any);

  const handleConsent = useCallback(
    debounce(async (data: SendMoneyState) => {
      // Prevent sending consent with a currency not allowed by current selection
      const allowedCurrencies = [
        data?.senderAccount?.balances?.[0]?.amount?.currency,
        data?.recipientAccountCurrency,
      ].filter(Boolean) as string[];
      const currentCurrency = data?.amount?.currency;
      if (currentCurrency && !allowedCurrencies.includes(currentCurrency)) {
        setIsLoading(false);
        return;
      }
      const payload = {
        sender: {
          accountId: data?.senderAccountId,
        },
        recipient: {
          accountId: data?.recipientAccountId,
        },
        amount: {
          value: data?.amount?.value,
          currency: data?.amount?.currency,
        },
        note: data?.note,
      } as SendMoneyConsentData;
      const consentId = data?.consentId;
      try {
        const res = consentId
          ? await updateSendMoneyConsent(consentId, payload)
          : await createSendMoneyConsent(payload);
        setConsent(res);
        setMainState((prevState: any) => ({
          ...prevState,
          consentId: res?.id,
        }));
      } catch (error: any) {
        if (error.response?.data?.message) {
          void message.error(error.response?.data?.message);
        }
        setConsent(({ id }) => ({ id } as any));
        setMainState((prevState: any) => ({
          ...prevState,
          consentId: '',
        }));
      }
      setIsLoading(false);
    }, 1000),
    []
  );

  useEffect(() => {
    const { amount, senderAccountId } = mainState;
    // Check if selected currency is still allowed
    const allowedCurrencies = [
      mainState?.senderAccount?.balances?.[0]?.amount?.currency,
      mainState?.recipientAccountCurrency,
    ].filter(Boolean) as string[];
    const currentCurrency = amount?.currency;
    if (
      currentCurrency &&
      allowedCurrencies.length > 0 &&
      !allowedCurrencies.includes(currentCurrency)
    ) {
      setIsLoading(false);
      setMainState((prev) => ({
        ...prev,
        amount: { value: prev.amount?.value || 0, currency: '' },
      }));
      form.setFieldsValue({ amount: { value: amount?.value || 0, currency: '' } });
      return;
    }
    if ((amount?.value || 0) > 0 && amount?.currency && senderAccountId) {
      setIsLoading(true);
      void handleConsent(mainState);
    } else {
      setConsent(({ id }) => ({ id } as any));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainState.amount, mainState.senderAccountId, mainState.note]);

  const debouncedSetNote = useCallback(
    debounce((note: string) => {
      setMainState((prev: any) => ({ ...prev, note }));
    }, 300),
    []
  );

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <TransferIcon style={{ fontSize: '80px', color: theme.primaryColor }} />
        <Typography.Title level={4} style={{ marginTop: '8px' }}>
          {beneficiaries[selectedBeneficiary!]?.beneficiary.name}
        </Typography.Title>
      </div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          recipientAccountNumber: recipientAccountNumber,
          recipientPhoneNumber: recipientPhoneNumber,
        }}
        onValuesChange={(_, data: any) =>
          setMainState((prev) => ({ ...prev, amount: data?.amount }))
        }
      >
        {recipientAccountNumber && (
          <Form.Item
            name={'recipientAccountNumber'}
            label={<Typography.Title level={4}>{t('recipientAccountNumber')}</Typography.Title>}
          >
            <Input disabled readOnly />
          </Form.Item>
        )}
        {recipientPhoneNumber && (
          <Form.Item
            name={'recipientPhoneNumber'}
            label={<Typography.Title level={4}>{t('recipientPhoneNumber')}</Typography.Title>}
          >
            <Input disabled readOnly />
          </Form.Item>
        )}
        <Form.Item label={<Typography.Title level={4}>{t('note')}</Typography.Title>}>
          <Input placeholder={t('note')} onChange={(e) => debouncedSetNote(e.target.value)} />
        </Form.Item>
        <FromAccount mainState={mainState} setMainState={setMainState} />
        <Wrapper direction="vertical" size={16}>
          <SendAmount
            consent={consent}
            loading={isLoading}
            setMainState={setMainState}
            form={form}
            disabled={!mainState.senderAccountId || !mainState.recipientAccountId}
            currencyFilter={(currency: string) =>
              mainState?.senderAccount?.balances?.[0]?.amount?.currency === currency ||
              mainState?.recipientAccountCurrency === currency
            }
          />
        </Wrapper>
        {consent?.totalAmount && !isLoading ? (
          <>
            <ConsentDetails consent={consent} />
            <ViewMoreDetails result={consent} />
            <Button
              type="primary"
              block
              onClick={onTransfer}
              style={{ marginTop: 16 }}
              loading={isLoading}
            >
              {t('transfer', {
                amount: getAmountText(consent?.totalAmount, true),
              })}
            </Button>
          </>
        ) : null}
      </Form>
    </div>
  );
};

export default Confirmation;
